import { uid } from './id.js';
import { updateArrowEndpoints } from './linkAnchors.js';

const CARD_W = 240;
const CARD_H = 148;

function idKey(raw) {
  if (raw == null) return null;
  return String(raw);
}

function defaultPos(i) {
  const col = i % 4;
  const row = Math.floor(i / 4);
  return { x: 80 + col * 280, y: 80 + row * 180 };
}

function frameById(elements, frameId) {
  return elements.find((e) => e.type === 'frame' && e.id === frameId);
}

function pushEdgeFromRecord(elements, edge, idx, cardElById) {
  const srcCard =
    edge.source_card_id != null ? cardElById[idKey(edge.source_card_id)] : null;
  const tgtCard =
    edge.target_card_id != null ? cardElById[idKey(edge.target_card_id)] : null;
  const srcFrame = edge.source_frame_id ? frameById(elements, edge.source_frame_id) : null;
  const tgtFrame = edge.target_frame_id ? frameById(elements, edge.target_frame_id) : null;
  const src = srcCard || srcFrame;
  const tgt = tgtCard || tgtFrame;
  if (!src || !tgt) return;
  const arrow = {
    id: edge.id || `edge-${idx}-${uid('a')}`,
    type: 'arrow',
    points: [
      [0, 0],
      [0, 0],
    ],
    strokeColor: edge.color || null,
    connectionType: edge.connection_type || 'related_to',
    connectionLabel: edge.label || null,
    endArrowhead: edge.end_arrowhead || 'arrow',
    startArrowhead: edge.start_arrowhead ?? null,
  };
  if (edge.source_card_id != null) arrow.sourceCardId = edge.source_card_id;
  if (edge.target_card_id != null) arrow.targetCardId = edge.target_card_id;
  if (edge.source_frame_id) arrow.sourceFrameId = edge.source_frame_id;
  if (edge.target_frame_id) arrow.targetFrameId = edge.target_frame_id;
  updateArrowEndpoints(arrow, elements);
  elements.push(arrow);
}

function edgeCardRef(value) {
  if (value == null) return value;
  const asNum = Number(value);
  if (Number.isFinite(asNum) && String(asNum) === String(value)) return asNum;
  return value;
}

/**
 * 将旧版 positions + edges 转为白板 elements（version 2）。
 */
export function migrateLegacy(cards, positions, edges, existingElements) {
  if (existingElements && existingElements.length > 0) {
    const known = new Set(
      (cards || []).map((c) => idKey(c.card_id)).filter(Boolean),
    );
    let elements = JSON.parse(JSON.stringify(existingElements));
    // Drop orphan card elements whose cardId is not in the current card list
    // (typical after sync when numeric server ids linger beside local UUIDs).
    elements = elements.filter((e) => {
      if (e.type !== 'card') return true;
      const key = idKey(e.cardId);
      return key != null && known.has(key);
    });
    const cardIds = new Set(
      elements
        .filter((e) => e.type === 'card' && e.cardId != null)
        .map((e) => idKey(e.cardId))
        .filter(Boolean),
    );
    cards.forEach((card, i) => {
      const key = idKey(card.card_id);
      if (!key || cardIds.has(key)) return;
      const stored = positions[key] || positions[String(card.card_id)];
      const pos =
        stored && stored.x != null
          ? { x: Number(stored.x), y: Number(stored.y) }
          : defaultPos(i);
      elements.push({
        id: `card-${card.card_id}`,
        type: 'card',
        cardId: card.card_id,
        x: pos.x,
        y: pos.y,
        width: CARD_W,
        height: CARD_H,
        title: card.card_name || '',
        body: card.card_content || card.card_notes || '',
        cardType: card.card_type || card.payload?.type || 'note',
        fileId: card.payload?.fileId ?? null,
        color: card.payload?.color || null,
        pageUrl: card.payload?.link || card.card_link || '',
        pageTitle: card.payload?.title || '',
        pageFavicon: card.payload?.favicon || '',
        embeddable: card.payload?.embeddable,
        pageScale: Number(card.payload?.pageScale) > 0 ? Number(card.payload.pageScale) : 0.5,
      });
      cardIds.add(key);
    });
    return elements;
  }

  const elements = [];
  const cardElById = {};

  cards.forEach((card, i) => {
    const key = idKey(card.card_id);
    const stored = positions[key] || positions[String(card.card_id)];
    const pos =
      stored && stored.x != null
        ? { x: Number(stored.x), y: Number(stored.y) }
        : defaultPos(i);
    const el = {
      id: `card-${card.card_id}`,
      type: 'card',
      cardId: card.card_id,
      x: pos.x,
      y: pos.y,
      width: CARD_W,
      height: CARD_H,
      title: card.card_name || '',
      body: card.card_content || card.card_notes || '',
      cardType: card.card_type || card.payload?.type || 'note',
      fileId: card.payload?.fileId ?? null,
      color: card.payload?.color || null,
      pageUrl: card.payload?.link || card.card_link || '',
      pageTitle: card.payload?.title || '',
      pageFavicon: card.payload?.favicon || '',
      embeddable: card.payload?.embeddable,
      pageScale: Number(card.payload?.pageScale) > 0 ? Number(card.payload.pageScale) : 0.5,
    };
    elements.push(el);
    if (key) cardElById[key] = el;
  });

  (edges || []).forEach((edge, idx) => {
    pushEdgeFromRecord(elements, edge, idx, cardElById);
  });

  return elements;
}

export function exportLegacy(elements) {
  const positions = {};
  const edges = [];

  for (const el of elements) {
    if (el.type === 'card' && el.cardId != null) {
      positions[String(el.cardId)] = { x: el.x, y: el.y };
    }
    if (el.type !== 'arrow') continue;
    const hasSource = el.sourceCardId != null || el.sourceFrameId;
    const hasTarget = el.targetCardId != null || el.targetFrameId;
    if (!hasSource || !hasTarget) continue;
    const edge = {
      id: el.id,
      connection_type: el.connectionType || 'related_to',
      end_arrowhead: el.endArrowhead || 'arrow',
      start_arrowhead: el.startArrowhead ?? null,
    };
    if (el.connectionLabel) edge.label = el.connectionLabel;
    if (el.strokeColor) edge.color = el.strokeColor;
    if (el.sourceCardId != null) edge.source_card_id = edgeCardRef(el.sourceCardId);
    if (el.targetCardId != null) edge.target_card_id = edgeCardRef(el.targetCardId);
    if (el.sourceFrameId) edge.source_frame_id = el.sourceFrameId;
    if (el.targetFrameId) edge.target_frame_id = el.targetFrameId;
    edges.push(edge);
  }

  return { positions, edges, elements };
}
